import Avatar from '@shared/components/Avatar/Avatar';
import ListItem from '@shared/components/ListItem/ListItem';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Mail } from 'assets/icons/Mail.svg';
import { ReactComponent as PhoneCall } from 'assets/icons/PhoneCall.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useMatrixUserAvatarBlobUrl from 'hooks/useMatrixUserAvatarBlobUrl/useMatrixUserAvatarBlobUrl';
import { getDefaultRoleTranslation } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { SetState } from 'utils/types';

import ExpirationBadge from './ExpirationBadge';
import MemberOptionsDropdown from './MemberOptionsDropdown';
import PendingInviteBadge from './PendingInviteBadge';
import RoleDropdown from '../../RoleDropdown';
import useRoleChange from '../useRoleChange';
import { Member } from '../utils';

interface MemberListItemProps {
  member: Member;
  onClick: SetState<string | null>;
}

const MemberListItem = ({ member, onClick }: MemberListItemProps) => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const changeRole = useRoleChange();
  const { workspacePermissions, workspaceId } = useWorkspacesContext();

  const currentUserId = matrixClient.getUserId();
  const isCurrentUser = currentUserId === member.id;
  const currentUserPowerLevel =
    matrixClient.getRoom(workspaceId ?? '')?.getMember(currentUserId ?? '')?.powerLevel ?? 0;
  const isTargetMemberLowerPowerLevel = currentUserPowerLevel > member.powerLevel;

  const { id, name, email, phone, inviteExpiration, roleName, roleId, defaultRoleType, isPendingInvite } = member;
  const room = workspaceId ? matrixClient.getRoom(workspaceId) : null;
  const avatarUrl = useMatrixUserAvatarBlobUrl(matrixClient, isPendingInvite ? undefined : id, room);
  const displayName = name || email || phone;
  const isRowDisabled = !isTargetMemberLowerPowerLevel || isCurrentUser || !workspacePermissions.adjustRoles;
  const hasEmailSubtitle = name && email && !inviteExpiration && !isPendingInvite;
  const formattedRoleName = getDefaultRoleTranslation(defaultRoleType, roleName) || roleName;
  const isOptionsDropdownVisible = workspacePermissions.kickMember && isTargetMemberLowerPowerLevel && !isCurrentUser;

  const handleClick = () => {
    if (!isRowDisabled) onClick(id);
  };

  const handleRoleChange = (newRoleId: string) => changeRole(member.id, newRoleId);

  const getAvatarIcon = () => {
    if (name) return;
    if (email) return Mail;
    if (phone) return PhoneCall;
  };

  return (
    <ListItem
      className="h-full !overflow-visible first:pt-0 last:pb-0 md:gap-2 gap-4"
      onClick={handleClick}
      hasEffects={!isRowDisabled}
      isMobileColumnLayout
    >
      <ListItem.LeftSide>
        <div className="shrink-0">
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className="h-10 w-10 rounded-xl object-cover" />
          ) : (
            <Avatar name={displayName} size="sm" fallbackIcon={getAvatarIcon()} iconClassName="stroke-1.5" />
          )}
        </div>
        <div className="flex flex-col space-y-px overflow-hidden">
          <p className="text-size-sm text-black truncate flex gap-1">
            <span className="font-medium truncate" translate="no">
              {name || email || phone}
            </span>
            {isCurrentUser && <span>({t('you')})</span>}
          </p>
          {isPendingInvite && <PendingInviteBadge />}
          {!isPendingInvite && inviteExpiration && <ExpirationBadge inviteExpiration={inviteExpiration} />}
          {hasEmailSubtitle && <span className="text-size-xs text-gray-80 truncate">{email}</span>}
        </div>
      </ListItem.LeftSide>
      <ListItem.RightSide className="h-full justify-between space-x-2 w-full md:w-auto">
        {isRowDisabled ? (
          <span className="text-size-sm text-gray-70 truncate">{formattedRoleName}</span>
        ) : (
          <RoleDropdown roleId={roleId} roleName={formattedRoleName} onRoleChange={handleRoleChange} />
        )}
        {isOptionsDropdownVisible && <MemberOptionsDropdown member={member} />}
      </ListItem.RightSide>
    </ListItem>
  );
};

export default MemberListItem;
